import { ReactElement, useState } from 'react'

import Dropdown from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import IconButton from 'components/v2/IconButton'
import List from 'components/v2/List'
import { QrCodeIcon } from 'components/Icons/QrIcon'
import { DownloadPdfLink } from 'features/BusinessCustomer/components/DownloadPdf'
import { GTBusinessCustomer } from 'gql/types.generated'
import { mapBusinessCustomerUserAccountToPdf } from 'features/BusinessCustomer/utils'
import styled from 'styled-components'
import { SwitchIcon } from 'components/Icons/SwitchIcon'
import {
  ACTIVATE_BUSINESS_CUSTOMER,
  DEACTIVATE_BUSINESS_CUSTOMER,
  REQUEST_RESET_USER_ACCOUNT_PASSWORD,
  UNASSIGN_BUSINESS_CUSTOMER_CORPORATE_CREDITS
} from 'features/BusinessCustomer/mutations-legacy'
import { ApolloCache, useMutation } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import { WarningIcon } from 'components/Icons/WarningIcon'
import { PasswordIcon } from 'components/Icons/PasswordIcon'

import { isEmpty } from 'lodash'
import { PencilIcon } from 'components/Icons/PencilIcon'
import Modal from 'components/v2/Modal'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { getUnprocessableErrors } from 'utils/error'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { CustomerPlaceIcon } from 'components/Icons/CustomerPlace'
import { EditBusinessCustomerPlaceModal } from 'features/BusinessCustomer/components/EditBusinessCustomerPlaceModal'

const ErrorContainer = styled.div`
  display: block;
  margin-top: 10px;
  text-align: center;
`
const DeactivateWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledWarningIcon = styled(WarningIcon)`
  margin-left: 10px;
  fill: rgb(245, 46, 46);
`

interface IBusinessCustomerActionsProps {
  disabled: boolean
  isKreditEnabled: boolean
  businessCustomer: GTBusinessCustomer
  setResetPasswordModal: (customerUserAccount: GTBusinessCustomer['userAccount']) => void
}
export const updateBusinessCustomerStatus = (
  businessCustomerId: string,
  cache: ApolloCache<TSFixMe>,
  isEnabled: boolean
): void => {
  cache.modify({
    id: cache.identify({
      __typename: 'BusinessCustomer',
      id: businessCustomerId
    }),
    fields: {
      isEnabled: () => isEnabled
    }
  })
}
export const BusinessCustomerListActions = ({
  isKreditEnabled,
  businessCustomer,
  disabled,
  setResetPasswordModal
}: IBusinessCustomerActionsProps): ReactElement => {
  const login = useLogin()
  const [isOpenDropdown, setOpenDropdown] = useState(false)
  const [isUnassignCorporateCreditsModalOpen, setUnassignCorporateCreditsModalOpen] = useState(false)
  const [isEditBusinessCustomerPlaceModalOpen, setEditBusinessCustomerPlaceModalOpen] = useState(false)

  const [unassignCorporateCredits, { loading: unassignCorporateCreditsLoading, error: unassignCorporateCreditsError }] =
    useMutation(UNASSIGN_BUSINESS_CUSTOMER_CORPORATE_CREDITS, {
      onCompleted: (): void => {
        setUnassignCorporateCreditsModalOpen(false)
      },
      onError: () => {
        //Do nothing
      }
    })

  const [activateBusinessCustomer, { loading: loadingActivation, error: activationError }] = useMutation(
    ACTIVATE_BUSINESS_CUSTOMER,
    {
      update(cache) {
        updateBusinessCustomerStatus(businessCustomer.id, cache, true)
      }
    }
  )

  const [deactivateBusinessCustomer, { loading: loadingDeactivation, error: deactivationError }] = useMutation(
    DEACTIVATE_BUSINESS_CUSTOMER,
    {
      update(cache) {
        updateBusinessCustomerStatus(businessCustomer.id, cache, false)
      }
    }
  )

  const [requestResetUserAccountPassword, { error: resetPasswordRequestError, loading: resetPasswordRequestLoading }] =
    useMutation(REQUEST_RESET_USER_ACCOUNT_PASSWORD, {
      onCompleted: (): void => {
        setResetPasswordModal(businessCustomer.userAccount)
      }
    })

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  const toggleActivation = (): void => {
    if (businessCustomer.isEnabled) {
      deactivateBusinessCustomer({
        variables: {
          input: { id: businessCustomer.id }
        }
      })
    } else {
      activateBusinessCustomer({
        variables: {
          input: { id: businessCustomer.id }
        }
      })
    }
  }

  const resetPassword = (): void => {
    requestResetUserAccountPassword({
      variables: {
        input: { email: businessCustomer.userAccount.email }
      }
    })
  }
  const isResetPasswordDisabled = isEmpty(businessCustomer.userAccount.email) || !businessCustomer.isEnabled

  const isUnassignCorporateCreditsDisabled =
    !canWrite(FEATURES.CORPORATE_KREDITS, login.accessToken.policies) || !isKreditEnabled || !businessCustomer.isEnabled
  const canEditBusinessCustomer = canWrite(FEATURES.BUSINESS_CUSTOMER, login.accessToken.policies)
  const canEditPlace = canEditBusinessCustomer && !!businessCustomer.place?.id

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): void => setOpenDropdown(false)}>
        <IconButton disabled={disabled} icon={<DotsIcon />} noBorder smallPadding onClick={toggleDropdown} />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.ItemLink href={`/business-customers/${businessCustomer.id}`} disabled={!canEditBusinessCustomer}>
              <List.Icon>
                <PencilIcon />
              </List.Icon>
              <FormattedMessage id="dictionary.edit" defaultMessage="Edit" />
            </List.ItemLink>
            <List.Item
              action
              onClick={toggleActivation}
              loading={loadingActivation || loadingDeactivation}
              disabled={!businessCustomer.isEnabled && !businessCustomer.businessClient.isActive}
            >
              <List.Icon>
                <SwitchIcon />
              </List.Icon>
              <DeactivateWrapper>
                {businessCustomer.isEnabled ? (
                  <FormattedMessage id="dictionary.deactivate" defaultMessage="Deactivate" />
                ) : (
                  <FormattedMessage id="dictionary.activate" defaultMessage="Activate" />
                )}
                {(deactivationError || activationError) && <StyledWarningIcon />}
              </DeactivateWrapper>
            </List.Item>
            {businessCustomer.isEnabled ? (
              <List.Item action>
                <List.Icon>
                  <QrCodeIcon />
                </List.Icon>
                <DownloadPdfLink
                  data={mapBusinessCustomerUserAccountToPdf(businessCustomer.qrCode, businessCustomer.userAccount)}
                />
              </List.Item>
            ) : (
              <List.Item disabled>
                <List.Icon>
                  <QrCodeIcon />
                </List.Icon>
                <FormattedMessage id="businessCustomer.downloadPdf" defaultMessage="Download QR" />
              </List.Item>
            )}
            <List.Item
              action
              onClick={(): void => {
                if (canEditPlace) {
                  setEditBusinessCustomerPlaceModalOpen(true)
                }
              }}
              disabled={!canEditPlace}
            >
              <List.Icon>
                <CustomerPlaceIcon />
              </List.Icon>
              <FormattedMessage id="businessCustomer.editPlace" defaultMessage="Edit Place" />
              {unassignCorporateCreditsError && <StyledWarningIcon />}
            </List.Item>
            <List.Item
              action
              loading={resetPasswordRequestLoading}
              onClick={(): void => {
                if (!isResetPasswordDisabled) {
                  resetPassword()
                }
              }}
              disabled={isResetPasswordDisabled}
            >
              <List.Icon>
                <PasswordIcon />
              </List.Icon>
              <FormattedMessage id="businessCustomer.resetPassword" defaultMessage="Reset password" />
              {resetPasswordRequestError && <StyledWarningIcon />}
            </List.Item>
            <List.Item
              action
              onClick={(): void => {
                if (!isUnassignCorporateCreditsDisabled) {
                  setUnassignCorporateCreditsModalOpen(true)
                }
              }}
              disabled={isUnassignCorporateCreditsDisabled}
            >
              <List.Icon>
                <SwitchIcon />
              </List.Icon>
              <FormattedMessage id="businessCustomer.uncategorize" defaultMessage="Uncategorize" />
              {unassignCorporateCreditsError && <StyledWarningIcon />}
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isUnassignCorporateCreditsModalOpen ? (
        <Modal.Container onClose={(): void => setUnassignCorporateCreditsModalOpen(false)}>
          <Modal.Header>
            <FormattedMessage
              id="businessCustomer.unassignCorporateCreditsWarning"
              defaultMessage="Are you sure you want to uncategorize the user {id}?"
              values={{ id: businessCustomer.id.substring(0, 8) }}
            />
          </Modal.Header>
          {unassignCorporateCreditsError ? (
            <ErrorContainer>
              <ErrorMessage errors={getUnprocessableErrors(unassignCorporateCreditsError)}>
                <FormattedMessage
                  id="businessCustomer.unassignCorporateCreditsError"
                  defaultMessage="Error unassigning corporate credits"
                />
              </ErrorMessage>
            </ErrorContainer>
          ) : null}
          <Modal.Actions>
            <Button kind="cancel" onClick={(): void => setUnassignCorporateCreditsModalOpen(false)}>
              <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              onClick={(): void => {
                unassignCorporateCredits({
                  variables: {
                    input: {
                      id: businessCustomer.id
                    }
                  }
                })
              }}
              loading={unassignCorporateCreditsLoading}
            >
              <FormattedMessage id="dictionary.accept" defaultMessage="Accept" />
            </Button>
          </Modal.Actions>
        </Modal.Container>
      ) : null}
      {isEditBusinessCustomerPlaceModalOpen ? (
        <EditBusinessCustomerPlaceModal
          businessCustomer={businessCustomer}
          onClose={(): void => setEditBusinessCustomerPlaceModalOpen(false)}
        />
      ) : null}
    </>
  )
}
